/**
 * Render a model's HTML table as an actual table.
 *
 * SANITISED, NOT TRUSTED. This is HTML produced by a model from a PDF that a
 * user uploaded — an untrusted document is upstream of it, so it is exactly the
 * shape of an injection: a crafted PDF whose "table" contains a script or an
 * event handler would otherwise execute in the viewer's session. DOMPurify with
 * an allowlist of table tags and the two span attributes admits the structure
 * and nothing else. No amount of "it's only OCR output" makes
 * dangerouslySetInnerHTML on it safe.
 */

import DOMPurify from "dompurify";
import { useMemo, useState } from "react";

const ALLOWED_TAGS = ["table", "thead", "tbody", "tfoot", "tr", "td", "th", "br", "b", "i", "em", "strong", "sup", "sub"];
const ALLOWED_ATTR = ["colspan", "rowspan"];

export function isTableHtml(text: string) {
  return /<table[\s>]/i.test(text);
}

export function TableBlock({ html }: { html: string }) {
  const [raw, setRaw] = useState(false);

  const clean = useMemo(
    () =>
      DOMPurify.sanitize(html, {
        ALLOWED_TAGS,
        ALLOWED_ATTR,
        // Belt and braces: no URLs survive the tag allowlist anyway, since no
        // tag that carries one is permitted.
        FORBID_TAGS: ["script", "style", "iframe", "object", "embed", "form", "input", "a", "img"],
        FORBID_ATTR: ["style", "class", "id", "href", "src", "srcset", "onerror", "onload"],
      }),
    [html],
  );

  // A model emitting cells with no <tr> is a REAL result, not something to
  // paper over: the browser then collapses every cell into one row, which is
  // visible here and is a genuine quality difference between models.
  const hasRows = /<tr[\s>]/i.test(clean);

  return (
    <div className="mt-1">
      <div className="mb-1 flex items-center gap-2">
        <button
          onClick={() => setRaw((v) => !v)}
          className="text-fh-meta text-fh-text-secondary underline-offset-2 hover:underline"
        >
          {raw ? "Show table" : "Show HTML"}
        </button>
        {!hasRows && (
          <span className="text-fh-meta text-fh-priority-normal">
            No &lt;tr&gt; rows emitted — cells collapse into one row
          </span>
        )}
      </div>

      {raw ? (
        <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-words rounded-fh-input bg-fh-surface-soft p-2 font-mono text-fh-meta text-fh-text-body">
          {html}
        </pre>
      ) : (
        <div className="max-w-full overflow-x-auto rounded-fh-input ring-1 ring-fh-border-soft">
          <div
            className="ocr-table"
            // Safe: `clean` is DOMPurify output restricted to the table tags above.
            dangerouslySetInnerHTML={{ __html: clean }}
          />
        </div>
      )}
    </div>
  );
}
